/**
 * API Configuration
 * Centralized configuration for API endpoints and settings
 */

function normalizeBaseUrl(value?: string | null): string {
  if (!value) {
    return '';
  }

  const trimmed = value.trim();

  if (trimmed === '' || trimmed === '/' || trimmed === './') {
    return '';
  }

  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
}

// Get API URL from environment variables or use default
function getApiBaseUrl() {
  // Check process.env first (build-time)
  const envUrl = normalizeBaseUrl(process.env.REACT_APP_API_URL);
  if (envUrl) {
    console.log('Using process.env.REACT_APP_API_URL:', envUrl);
    return envUrl;
  }
  
  // Check window variables (runtime)
  if (typeof window !== 'undefined') {
    const runtimeUrl = normalizeBaseUrl(
      (window as any).REACT_APP_API_URL || (window as any).API_BASE_URL
    );
    if (runtimeUrl) {
      console.log('Using runtime API URL:', runtimeUrl);
      return runtimeUrl;
    }
  }
  
  // Fallback to same-origin relative requests
  console.log('Using same-origin API URL fallback (relative /api)');
  return '';
}

export const API_BASE_URL = getApiBaseUrl();

// API endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    LOGOUT: '/api/auth/logout',
    ME: '/api/auth/me',
    REFRESH: '/api/auth/refresh',
  },
  
  // Email
  EMAIL: {
    SEND_VERIFICATION: '/api/email/send-verification',
    SEND_PASSWORD_RESET: '/api/email/send-password-reset',
  },
  
  // Companies
  COMPANIES: {
    LIST: '/api/companies',
    CREATE: '/api/companies',
    GET: (id: string) => `/api/companies/${id}`,
    UPDATE: (id: string) => `/api/companies/${id}`,
    DELETE: (id: string) => `/api/companies/${id}`,
  },
  
  // Locations
  LOCATIONS: {
    LIST: '/api/locations',
    CREATE: '/api/locations',
    GET: (id: string) => `/api/locations/${id}`,
    UPDATE: (id: string) => `/api/locations/${id}`,
    DELETE: (id: string) => `/api/locations/${id}`,
  },
  
  // Users
  USERS: {
    LIST: '/api/users',
    CREATE: '/api/users',
    GET: (id: string) => `/api/users/${id}`,
    UPDATE: (id: string) => `/api/users/${id}`,
    DELETE: (id: string) => `/api/users/${id}`,
  },
  
  // Clients
  CLIENTS: {
    LIST: '/api/clients',
    CREATE: '/api/clients',
    GET: (id: string) => `/api/clients/${id}`,
    UPDATE: (id: string) => `/api/clients/${id}`,
    DELETE: (id: string) => `/api/clients/${id}`,
  },
  
  // Visits
  VISITS: {
    LIST: '/api/visits',
    CREATE: '/api/visits',
    GET: (id: string) => `/api/visits/${id}`,
    UPDATE: (id: string) => `/api/visits/${id}`,
    DELETE: (id: string) => `/api/visits/${id}`,
  },
  
  // Treatments
  TREATMENTS: {
    LIST: '/api/treatments',
    CREATE: '/api/treatments',
    GET: (id: string) => `/api/treatments/${id}`,
    UPDATE: (id: string) => `/api/treatments/${id}`,
    DELETE: (id: string) => `/api/treatments/${id}`,
    GET_BY_VISIT: (visitId: string) => `/api/treatments/visit/${visitId}`,
    GET_PHOTOS: (id: string) => `/api/treatments/${id}/photos`,
  },
  
  // Uploads
  UPLOADS: {
    UPLOAD_PHOTO: '/api/uploads/photo',
    GET_PHOTO: (id: string) => `/api/uploads/photo/${id}`,
    DOWNLOAD_PHOTO: (id: string) => `/api/uploads/photo/${id}/download`,
    UPDATE_PHOTO: (id: string) => `/api/uploads/photo/${id}`,
    DELETE_PHOTO: (id: string) => `/api/uploads/photo/${id}`,
    GET_CLIENT_PHOTOS: (clientId: string) => `/api/uploads/client/${clientId}/photos`,
  },
  
  // Health & Info
  HEALTH: '/api/health',
  API_INFO: '/api',
};

// Helper function to build full API URL
export function buildApiUrl(endpoint: string): string {
  return `${API_BASE_URL}${endpoint}`;
}

// Default headers for API requests
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};

// API configuration
export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  TIMEOUT: 10000, // 10 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
};
